import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Lock, Unlock, Plus, LogOut, Target, 
  DollarSign, Loader2, Bell, BarChart3, 
  Crown, Building2, ArrowDownToLine, Clock,
  ChevronRight, Vault, Smartphone, X, Apple, Users, Receipt, HeartHandshake, LayoutDashboard
} from 'lucide-react';
import { CreateVaultModal, DepositModal, VaultDetailModal } from './Modals';
import { NudgeCard } from '../components/NudgeCard';
import { Avatar } from '../components/SocialBits';
import { PremiumModal, useSubscriptionVerify } from './PremiumModal';
import { ConnectBankModal, WithdrawToBankModal } from './ConnectBank';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function getTimeRemaining(unlockDate) {
  const now = new Date();
  const unlock = new Date(unlockDate);
  const diff = unlock - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, isUnlocked: true, text: 'Unlocked' };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  let text = '';
  if (days > 0) text = `${days}d ${hours}h`;
  else if (hours > 0) text = `${hours}h ${minutes}m`;
  else text = `${minutes}m ${seconds}s`;
  
  return { days, hours, minutes, seconds, isUnlocked: false, text };
}

function getNextUnlockDate(goals) {
  const locked = goals
    .filter(g => new Date(g.unlock_date) > new Date())
    .sort((a, b) => new Date(a.unlock_date) - new Date(b.unlock_date));
  return locked.length > 0 ? locked[0] : null;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showBankWithdrawModal, setShowBankWithdrawModal] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const { verify } = useSubscriptionVerify();

  const isPremium = user?.tier === 'premium';
  const canCreateGoal = isPremium || (goals.length < 3);

  const fetchData = useCallback(async () => {
    try {
      const [goalsRes, statsRes, connectRes] = await Promise.all([
        axios.get(`${API}/api/goals`, { withCredentials: true }),
        axios.get(`${API}/api/stats`, { withCredentials: true }),
        axios.get(`${API}/api/connect/status`, { withCredentials: true }).catch(() => ({ data: { connected: false } }))
      ]);
      setGoals(goalsRes.data);
      setStats(statsRes.data);
      setConnectStatus(connectRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const subscription = params.get('subscription');
    const connect = params.get('connect');
    if (sessionId && subscription === 'success') {
      verify(sessionId);
      window.history.replaceState({}, '', '/dashboard');
    } else if (connect === 'success') {
      toast.success('Bank account connected! You can now deposit and withdraw.');
      fetchData();
      window.history.replaceState({}, '', '/dashboard');
    } else if (connect === 'refresh') {
      setShowConnectModal(true);
      window.history.replaceState({}, '', '/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleCreateVault = () => {
    if (!canCreateGoal) {
      setShowPremiumModal(true);
      toast.error(`Free accounts can create up to 3 vaults. Upgrade for unlimited.`);
      return;
    }
    setShowCreateModal(true);
  };

  const openDeposit = (goal) => { setSelectedGoal(goal); setShowDepositModal(true); };
  const openDetail = (goal) => { setSelectedGoal(goal); setShowDetailModal(true); };

  const nextUnlock = getNextUnlockDate(goals);

  if (loading) {
    return (
      <div className="min-h-screen bg-vault flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#9333EA] mx-auto mb-4" />
          <p className="text-[#A19DB0] text-sm">Opening your vaults...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">FutureVault</span>
            </div>
            <div className="flex items-center gap-2">
              {connectStatus?.connected ? (
                <button data-testid="bank-connected-badge" onClick={() => setShowConnectModal(true)} className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                  <Building2 className="h-3 w-3 text-emerald-400" />
                  <span className="text-[10px] font-bold text-emerald-400">BANK</span>
                </button>
              ) : (
                <Button data-testid="connect-bank-nav-button" size="sm" onClick={() => setShowConnectModal(true)} className="h-7 text-[10px] btn-secondary border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                  <Building2 className="h-3 w-3 mr-1" /> Connect Bank
                </Button>
              )}
              {isPremium ? (
                <button data-testid="premium-badge" onClick={() => setShowPremiumModal(true)} className="flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-[#9333EA]/30 to-amber-500/30 border border-amber-500/30">
                  <Crown className="h-3 w-3 text-amber-400" />
                  <span className="text-[10px] font-bold text-amber-400">PRO</span>
                </button>
              ) : (
                <Button data-testid="upgrade-button" size="sm" onClick={() => setShowPremiumModal(true)} className="h-7 text-[10px] bg-gradient-to-r from-[#9333EA] to-amber-500 text-white font-bold">
                  <Crown className="h-3 w-3 mr-1" /> PRO
                </Button>
              )}
              <Link to="/friends">
                <Button variant="ghost" size="sm" data-testid="friends-link" className="h-7 w-7 p-0 text-[#A19DB0] hover:text-white hover:bg-white/5" title="Friends & Groups">
                  <HeartHandshake className="h-3.5 w-3.5" />
                </Button>
              </Link>
              <Link to="/my-refunds">
                <Button variant="ghost" size="sm" data-testid="my-refunds-link" className="h-7 w-7 p-0 text-[#A19DB0] hover:text-white hover:bg-white/5" title="My Refunds">
                  <Receipt className="h-3.5 w-3.5" />
                </Button>
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin/waitlist">
                  <Button variant="ghost" size="sm" data-testid="admin-waitlist-link" className="h-7 w-7 p-0 text-[#A19DB0] hover:text-white hover:bg-white/5" title="Waitlist">
                    <Users className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin/hub">
                  <Button variant="ghost" size="sm" data-testid="admin-hub-link" className="h-7 w-7 p-0 text-amber-400 hover:text-amber-300 hover:bg-white/5" title="Admin Hub">
                    <LayoutDashboard className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              )}
              <Link to="/profile" data-testid="profile-link" title="Profile" className="rounded-full ring-1 ring-white/10 hover:ring-[#9333EA] transition-shadow">
                <Avatar user={user} size={7} />
              </Link>
              <Button variant="ghost" size="sm" data-testid="logout-button" onClick={handleLogout} className="h-7 w-7 p-0 text-[#A19DB0] hover:text-white hover:bg-white/5">
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </header>      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Welcome */}
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
            Hey {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-[#A19DB0] text-sm mt-1">Here's what's locked away for your future.</p>
        </div>

        <MobileAppPromo />

        <NudgeCard onUpgrade={() => setShowPremiumModal(true)} />

        {/* At a Glance */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="p-5 bg-[#0A0A0F] rounded-xl border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-4 w-4 text-[#9333EA]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0]">Total Locked</span>
            </div>
            <div data-testid="stat-locked-funds" className="font-mono text-2xl font-bold text-[#9333EA]">
              {formatCurrency(stats?.locked_amount || 0)}
            </div>
            <p className="text-xs text-[#A19DB0] mt-1">Across {goals.filter(g => new Date(g.unlock_date) > new Date()).length} vault{goals.filter(g => new Date(g.unlock_date) > new Date()).length !== 1 ? 's' : ''}</p>
          </div>
          <div className="p-5 bg-[#0A0A0F] rounded-xl border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-[#A19DB0]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0]">Next Unlock</span>
            </div>
            {nextUnlock ? (
              <>
                <div className="font-mono text-2xl font-bold text-white">
                  <CountdownText unlockDate={nextUnlock.unlock_date} />
                </div>
                <p className="text-xs text-[#A19DB0] mt-1">{nextUnlock.name}</p>
              </>
            ) : (
              <>
                <div className="font-mono text-2xl font-bold text-[#A19DB0]">--</div>
                <p className="text-xs text-[#A19DB0] mt-1">No locked vaults</p>
              </>
            )}
          </div>
        </div>

        {/* Ready to Withdraw Banner */}
        {stats?.unlocked_amount > 0 && (
          <div data-testid="withdraw-banner" className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Unlock className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-semibold">{formatCurrency(stats.unlocked_amount)} ready to withdraw</p>
                <p className="text-xs text-emerald-400">Tap on an unlocked vault to send to your bank</p>
              </div>
            </div>
          </div>
        )}

        {/* Create Vault Button */}
        <Button
          data-testid="create-goal-button"
          onClick={handleCreateVault}
          className="w-full h-14 mb-6 btn-primary font-semibold text-base rounded-xl"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create New Vault
          {!canCreateGoal && <Crown className="h-4 w-4 text-amber-400 ml-2" />}
        </Button>

        {/* Vault List */}
        <div className="space-y-3">
          {goals.length === 0 ? (
            <div className="p-12 text-center bg-[#0A0A0F] rounded-xl border border-white/10">
              <div className="h-16 w-16 rounded-full bg-[#12121A] flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-[#9333EA]" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">
                No vaults yet
              </h3>
              <p className="text-[#A19DB0] text-sm mb-1">
                Create your first vault to start locking money away.
              </p>
              <p className="text-[#A19DB0] text-xs">
                It takes 30 seconds to set up.
              </p>
            </div>
          ) : (
            goals.map((goal) => (
              <VaultCard
                key={goal.goal_id}
                goal={goal}
                onDeposit={() => openDeposit(goal)}
                onTap={() => openDetail(goal)}
              />
            ))
          )}
        </div>
      </main>

      {/* Modals */}
      <CreateVaultModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={() => { fetchData(); toast.success('Vault created! Start adding deposits.'); }} />
      <PremiumModal open={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
      <ConnectBankModal open={showConnectModal} onClose={() => { setShowConnectModal(false); fetchData(); }} />
      {selectedGoal && (
        <>
          <DepositModal open={showDepositModal} onClose={() => { setShowDepositModal(false); setSelectedGoal(null); }} goal={selectedGoal} onSuccess={fetchData} connectStatus={connectStatus} />
          <VaultDetailModal open={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedGoal(null); }} goal={selectedGoal} onUpdate={fetchData} connectStatus={connectStatus} onBankWithdraw={() => { setShowDetailModal(false); setShowBankWithdrawModal(true); }} />
          <WithdrawToBankModal open={showBankWithdrawModal} onClose={() => { setShowBankWithdrawModal(false); setSelectedGoal(null); }} goal={selectedGoal} onSuccess={fetchData} />
        </>
      )}
    </div>
  );
}

function CountdownText({ unlockDate }) {
  const [time, setTime] = useState(getTimeRemaining(unlockDate));
  
  useEffect(() => {
    const interval = setInterval(() => setTime(getTimeRemaining(unlockDate)), 1000);
    return () => clearInterval(interval);
  }, [unlockDate]);

  return <span className="countdown-glow">{time.text}</span>;
}

function VaultCard({ goal, onDeposit, onTap }) {
  const [time, setTime] = useState(getTimeRemaining(goal.unlock_date));
  const progress = goal.target_amount > 0 ? Math.min((goal.current_amount / goal.target_amount) * 100, 100) : 0;

  useEffect(() => {
    const interval = setInterval(() => setTime(getTimeRemaining(goal.unlock_date)), 1000);
    return () => clearInterval(interval);
  }, [goal.unlock_date]);

  return (
    <div
      data-testid={`goal-card-${goal.goal_id}`}
      className={`p-5 rounded-xl border cursor-pointer transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] ${
        time.isUnlocked 
          ? 'bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10' 
          : 'bg-[#0A0A0F] border-white/10 hover:bg-[#12121A]'
      }`}
      onClick={onTap}
    >
      {/* Top Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
            time.isUnlocked ? 'bg-emerald-500/20' : 'bg-[#9333EA]/20'
          }`}>
            {time.isUnlocked ? <Unlock className="h-5 w-5 text-emerald-400" /> : <Lock className="h-5 w-5 text-[#9333EA]" />}
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white text-base">{goal.name}</h3>
            <span className={`text-xs font-semibold ${time.isUnlocked ? 'text-emerald-400' : 'text-[#A19DB0]'}`}>
              {time.isUnlocked ? 'Ready to withdraw' : `Unlocks ${new Date(goal.unlock_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            </span>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[#A19DB0]" />
      </div>

      {/* Amount */}
      <div className="flex items-baseline justify-between mb-3">
        <span data-testid={`goal-amount-${goal.goal_id}`} className="font-mono text-2xl font-bold text-white">
          {formatCurrency(goal.current_amount)}
        </span>
        <span className="text-sm text-[#A19DB0]">
          of {formatCurrency(goal.target_amount)}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="w-full h-2 bg-[#12121A] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${time.isUnlocked ? 'bg-emerald-400' : 'bg-[#9333EA]'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-[#A19DB0]">{progress.toFixed(0)}% saved</span>
          {!time.isUnlocked && (
            <span className="text-xs font-mono text-[#9333EA]">{time.text}</span>
          )}
        </div>
      </div>

      {/* Quick Deposit Button */}
      <div onClick={(e) => e.stopPropagation()}>
        <Button
          data-testid={`deposit-button-${goal.goal_id}`}
          onClick={onDeposit}
          size="sm"
          className={`w-full ${time.isUnlocked ? 'bg-emerald-500 hover:bg-emerald-400 text-white' : 'btn-primary'}`}
        >
          {time.isUnlocked ? (
            <><ArrowDownToLine className="h-4 w-4 mr-2" /> Withdraw to Bank</>
          ) : (
            <><DollarSign className="h-4 w-4 mr-2" /> Add to Vault</>
          )}
        </Button>
      </div>

      {/* Locked message */}
      {!time.isUnlocked && goal.current_amount > 0 && (
        <p className="text-center text-xs text-[#A19DB0] mt-2">
          This money is locked until {new Date(goal.unlock_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </div>
  );
}


function MobileAppPromo() {
  const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || '';
  const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || '';
  const storageKey = 'fv_mobile_promo_dismissed';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const liveApps = APP_STORE_URL || PLAY_STORE_URL;

  return (
    <div
      className="relative mb-6 p-4 rounded-xl border border-[#9333EA]/30 bg-gradient-to-r from-[#9333EA]/15 via-[#9333EA]/5 to-transparent overflow-hidden"
      data-testid="dashboard-mobile-promo"
    >
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center text-[#A19DB0] hover:text-white hover:bg-white/10 transition-colors"
        data-testid="dashboard-mobile-promo-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#9333EA] flex items-center justify-center flex-shrink-0">
          <Smartphone className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm">
            {liveApps ? 'FutureVault is on your phone.' : 'FutureVault mobile is coming soon.'}
          </p>
          <p className="text-xs text-[#A19DB0] mt-0.5">
            {liveApps ? 'Download the app and save on the go.' : 'Join the waitlist — we\'ll email you at launch.'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {APP_STORE_URL ? (
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black border border-white/20 hover:border-[#9333EA] text-white text-xs font-semibold transition-colors"
            data-testid="dashboard-app-store-link"
          >
            <Apple className="h-4 w-4" />
            App Store
          </a>
        ) : (
          <a
            href="/#mobile-waitlist"
            onClick={(e) => { e.preventDefault(); window.location.href = '/#mobile-waitlist'; }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#9333EA] hover:bg-[#7e22ce] text-white text-xs font-semibold transition-colors"
            data-testid="dashboard-ios-waitlist-link"
          >
            <Apple className="h-4 w-4" />
            Join iOS waitlist
          </a>
        )}
        {PLAY_STORE_URL && (
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black border border-white/20 hover:border-[#9333EA] text-white text-xs font-semibold transition-colors"
            data-testid="dashboard-play-store-link"
          >
            Google Play
          </a>
        )}
      </div>
    </div>
  );
}
